import { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { SunMedium, MoonStar, Palette, CheckCircle2, List, GripVertical, ChevronDown, ChevronRight, Pencil, Check, X } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useThemeStore, DEFAULT_DARK_BACKGROUND } from '../../store/themeStore';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { adminAPI } from '../../api';
import { useToast } from '../../hooks/useToast';
import { DEFAULT_TENANT_MENU_ITEMS } from '../../lib/tenantMenuItems';

const TAB_ITEMS = [
  { id: 'light', label: 'Light Mode', icon: SunMedium },
  { id: 'dark', label: 'Dark Mode', icon: MoonStar },
  { id: 'menu-order', label: 'Menu Order', icon: List },
];

const checklist = [
  'Ensure primary brand colour meets AA contrast requirements.',
  'Use a minimum 4.5:1 contrast ratio for text on backgrounds.',
  'Keep interactive states consistent across both themes.',
];

export default function AdminVisualSettings() {
  const [activeTab, setActiveTab] = useState('light');
  const { darkBackground, setDarkBackground, resetDarkBackground } = useThemeStore(
    useShallow((state) => ({
      darkBackground: state.darkBackground,
      setDarkBackground: state.setDarkBackground,
      resetDarkBackground: state.resetDarkBackground,
    }))
  );

  return (
    <Layout>
      {/* Header */}
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 flex items-center justify-center rounded-[var(--ds-radius-md)] bg-[var(--ds-status-success-bg)] text-[var(--ds-accent-primary)]">
              <Palette className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">Visual Settings</h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                Manage the default presentation for system light and dark themes.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Card>
          <div className="border-b border-[var(--ds-border-divider)]">
            <nav className="-mb-px flex">
              {TAB_ITEMS.map((tab) => {
                const Icon = tab.icon;
                const isActive = tab.id === activeTab;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 py-4 px-6 border-b-2 font-medium text-meta transition-colors ${
                      isActive
                        ? 'border-[var(--ds-accent-primary)] text-[var(--ds-accent-primary)]'
                        : 'border-transparent text-[var(--ds-text-muted)] hover:text-[var(--ds-text-secondary)] hover:border-[var(--ds-border-subtle)]'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          <CardContent className="p-6 space-y-6">
            {activeTab === 'light' && <LightModePanel />}
            {activeTab === 'dark' && (
              <DarkModePanel
                color={darkBackground}
                onColorChange={setDarkBackground}
                onReset={resetDarkBackground}
                defaultColor={DEFAULT_DARK_BACKGROUND}
              />
            )}
            {activeTab === 'menu-order' && <MenuOrderPanel />}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

function GuidanceCard({ title, description, accent = 'blue' }) {
  const accentClasses = {
    blue: 'bg-[var(--ds-status-info-bg)] text-[var(--ds-status-info)] border-[var(--ds-status-info)]',
    purple: 'bg-[var(--ds-status-warning-bg)] text-[var(--ds-status-warning)] border-[var(--ds-status-warning)]',
    indigo: 'bg-[var(--ds-control-field-bg)] text-[var(--ds-text-secondary)] border-[var(--ds-border-subtle)]',
  };

  return (
    <div className={`rounded-[var(--ds-radius-md)] border px-4 py-3 ${accentClasses[accent] || accentClasses.blue}`}>
      <h3 className="text-meta font-semibold mb-1">{title}</h3>
      <p className="text-meta opacity-90">{description}</p>
    </div>
  );
}

function Checklist() {
  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="text-meta font-semibold text-[var(--ds-text-primary)] mb-3">Accessibility checklist</h3>
        <ul className="space-y-2">
          {checklist.map((item) => (
            <li key={item} className="flex items-start gap-2 text-meta text-[var(--ds-text-secondary)]">
              <CheckCircle2 className="h-4 w-4 text-[var(--ds-status-success)] mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function LightModePanel() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-h2 font-semibold text-[var(--ds-text-primary)]">Light mode appearance</h2>
        <p className="text-meta text-[var(--ds-text-secondary)] mt-1">
          Define the primary palette and typography that will be used when users view SparxMate in light mode.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <GuidanceCard
          title="Primary colour"
          description="Choose a vibrant hue that complements the SparxMate blue brand accents."
        />
        <GuidanceCard
          title="Surface backgrounds"
          description="Use subtle grays (gray-50 & gray-100) for cards and navigation. Avoid pure white where glare may occur."
          accent="purple"
        />
        <GuidanceCard
          title="Form elements"
          description="Maintain the standard border, focus ring, and placeholder colours for consistency across tenants."
          accent="indigo"
        />
      </div>

      <Checklist />

      <div className="rounded-[var(--ds-radius-md)] border border-dashed border-[var(--ds-border-subtle)] p-6 text-center text-meta text-[var(--ds-text-muted)]">
        Detailed configuration controls for light mode branding will be available in a future phase.
      </div>
    </div>
  );
}

function DarkModePanel({ color, onColorChange, onReset, defaultColor }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-h2 font-semibold text-[var(--ds-text-primary)]">Dark mode appearance</h2>
        <p className="text-meta text-[var(--ds-text-secondary)] mt-1">
          Manage tones and contrast to keep interfaces legible and comfortable in low-light environments.
        </p>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="text-meta font-semibold text-[var(--ds-text-primary)]">Background colour</h3>
              <p className="text-meta text-[var(--ds-text-secondary)] mt-1">
                Select the baseline surface colour for dark mode across all admin and tenant views.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <label className="inline-flex items-center gap-3">
                <span className="sr-only">Dark mode background colour picker</span>
                <input
                  type="color"
                  value={color}
                  onChange={(event) => onColorChange(event.target.value)}
                  className="h-12 w-16 rounded-[var(--ds-radius-md)] border border-[var(--ds-control-field-border)] bg-[var(--ds-control-field-bg)]"
                />
                <span className="text-meta font-medium text-[var(--ds-text-primary)]">
                  {color?.toUpperCase()}
                </span>
              </label>
              <Button
                variant="secondary"
                size="sm"
                onClick={onReset}
                disabled={color?.toLowerCase() === defaultColor?.toLowerCase()}
              >
                Reset
              </Button>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="overflow-hidden">
              <div
                className="h-24 w-full"
                style={{ backgroundColor: color }}
                aria-hidden="true"
              />
              <div className="p-3 text-xs text-[var(--ds-text-muted)]">
                Live preview updates instantly for all dark-mode pages.
              </div>
            </Card>
            <div className="md:col-span-2 text-xs text-[var(--ds-text-muted)]">
              <p>
                The selected colour updates the global dark-mode surface token. Components that use standard
                <code className="mx-1 rounded-[var(--ds-radius-sm)] bg-[var(--ds-control-field-bg)] px-1 py-0.5">bg-background</code>
                and layout shells adopt this value automatically.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <GuidanceCard
          title="Base layers"
          description="Start with gray-900 for the page background and gray-800 for cards to mirror tenant UI standards."
        />
        <GuidanceCard
          title="Interactive states"
          description="Primary actions should use lighter tints of the brand colour (e.g., blue-500) with hover states one step brighter."
          accent="purple"
        />
        <GuidanceCard
          title="Typography"
          description="Use gray-100 for headings, gray-300 for body text, and gray-500 for helper copy to maintain hierarchy."
          accent="indigo"
        />
      </div>

      <Checklist />

      <div className="rounded-[var(--ds-radius-md)] border border-dashed border-[var(--ds-border-subtle)] p-6 text-center text-meta text-[var(--ds-text-muted)]">
        Dark mode theme overrides will connect to the design token system in a later milestone.
      </div>
    </div>
  );
}

function MenuOrderPanel() {
  const { success, error, ToastContainer } = useToast();
  const [items, setItems] = useState([...DEFAULT_TENANT_MENU_ITEMS]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedParents, setExpandedParents] = useState({});
  const [draggedItem, setDraggedItem] = useState(null); // { type: 'parent' | 'subItem', parentIndex: number, subItemIndex?: number }
  const [dropTarget, setDropTarget] = useState(null); // { type: 'parent' | 'subItem', parentIndex: number, subItemIndex?: number, position?: 'before' | 'after' | 'inside' }
  const [editingItem, setEditingItem] = useState(null); // { type: 'parent' | 'subItem', parentIndex: number, subItemIndex?: number }
  const [editValue, setEditValue] = useState('');

  // Build a map of all sub-items to their default parents for reference
  const subItemToDefaultParent = useCallback(() => {
    const map = new Map();
    DEFAULT_TENANT_MENU_ITEMS.forEach((parent) => {
      if (parent.subItems) {
        parent.subItems.forEach((subItem) => {
          map.set(subItem.path, parent.path);
        });
      }
    });
    return map;
  }, []);

  const applyOrder = useCallback((order) => {
    if (!Array.isArray(order) || order.length === 0) {
      setItems([...DEFAULT_TENANT_MENU_ITEMS]);
      return;
    }

    // Create maps for quick lookup
    const pathToDefaultItem = new Map();
    DEFAULT_TENANT_MENU_ITEMS.forEach((item) => {
      pathToDefaultItem.set(item.path, item);
    });

    const subItemToDefaultParentMap = subItemToDefaultParent();

    // Reconstruct items from order
    const ordered = [];
    const seenParentPaths = new Set();
    const seenSubItemPaths = new Set();

    for (const orderItem of order) {
      if (typeof orderItem === 'string') {
        // Legacy format: just paths
        const defaultItem = pathToDefaultItem.get(orderItem);
        if (defaultItem && !seenParentPaths.has(orderItem)) {
          ordered.push({ ...defaultItem });
          seenParentPaths.add(orderItem);
        }
      } else if (orderItem && typeof orderItem === 'object' && orderItem.path) {
        // New format: { path, label?: string, subItems: [...] }
        const defaultItem = pathToDefaultItem.get(orderItem.path);
        if (defaultItem && !seenParentPaths.has(orderItem.path)) {
          const reconstructedItem = { ...defaultItem };
          
          // Apply custom label if provided
          if (orderItem.label) {
            reconstructedItem.label = orderItem.label;
          }

          // Reconstruct sub-items from order
          if (orderItem.subItems && Array.isArray(orderItem.subItems)) {
            reconstructedItem.subItems = [];
            for (const subItemOrder of orderItem.subItems) {
              const subItemPath = typeof subItemOrder === 'string' ? subItemOrder : subItemOrder?.path;
              if (subItemPath && !seenSubItemPaths.has(subItemPath)) {
                // Find the sub-item in default structure
                const defaultSubItem = defaultItem.subItems?.find((si) => si.path === subItemPath);
                if (defaultSubItem) {
                  const reconstructedSubItem = { ...defaultSubItem };
                  // Apply custom label if provided
                  if (typeof subItemOrder === 'object' && subItemOrder.label) {
                    reconstructedSubItem.label = subItemOrder.label;
                  }
                  reconstructedItem.subItems.push(reconstructedSubItem);
                  seenSubItemPaths.add(subItemPath);
                }
              }
            }

            // Add any remaining default sub-items that weren't in the order
            if (defaultItem.subItems) {
              defaultItem.subItems.forEach((defaultSubItem) => {
                if (!seenSubItemPaths.has(defaultSubItem.path)) {
                  reconstructedItem.subItems.push({ ...defaultSubItem });
                  seenSubItemPaths.add(defaultSubItem.path);
                }
              });
            }
          }

          ordered.push(reconstructedItem);
          seenParentPaths.add(orderItem.path);
        }
      }
    }

    // Add any remaining default items that weren't in the order
    DEFAULT_TENANT_MENU_ITEMS.forEach((defaultItem) => {
      if (!seenParentPaths.has(defaultItem.path)) {
        ordered.push({ ...defaultItem });
      }
    });

    setItems(ordered);
  }, [subItemToDefaultParent]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminAPI
      .getMenuOrder()
      .then((res) => {
        if (cancelled) return;
        const order = Array.isArray(res) ? res : (res?.order || []);
        applyOrder(order);
      })
      .catch(() => {
        if (cancelled) return;
        applyOrder([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [applyOrder]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Convert items to API format: [{ path, label?, subItems: [{ path, label? }] }]
      const order = items.map((item) => {
        const defaultItem = DEFAULT_TENANT_MENU_ITEMS.find(di => di.path === item.path);
        const hasCustomLabel = defaultItem && item.label !== defaultItem.label;
        
        return {
          path: item.path,
          ...(hasCustomLabel && { label: item.label }),
          subItems: item.subItems ? item.subItems.map((subItem) => {
            const defaultSubItem = defaultItem?.subItems?.find(dsi => dsi.path === subItem.path);
            const hasCustomSubLabel = defaultSubItem && subItem.label !== defaultSubItem.label;
            
            return {
              path: subItem.path,
              ...(hasCustomSubLabel && { label: subItem.label }),
            };
          }) : [],
        };
      });
      await adminAPI.updateMenuOrder(order);
      success('Menu order saved', 'Tenant sidebar menu order has been updated.');
    } catch (err) {
      error('Failed to save', err?.message ?? 'Could not update menu order.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setItems([...DEFAULT_TENANT_MENU_ITEMS]);
    setExpandedParents({});
    setEditingItem(null);
    setEditValue('');
    success('Reset to default', 'Menu order has been reset to the default.');
  };

  const startEditing = (type, parentIndex, subItemIndex = null) => {
    if (type === 'parent') {
      const item = items[parentIndex];
      setEditingItem({ type: 'parent', parentIndex });
      setEditValue(item.label);
    } else if (type === 'subItem') {
      const subItem = items[parentIndex].subItems[subItemIndex];
      setEditingItem({ type: 'subItem', parentIndex, subItemIndex });
      setEditValue(subItem.label);
    }
  };

  const cancelEditing = () => {
    setEditingItem(null);
    setEditValue('');
  };

  const saveEdit = () => {
    if (!editingItem || !editValue.trim()) {
      cancelEditing();
      return;
    }

    setItems((prev) => {
      const next = [...prev];
      if (editingItem.type === 'parent') {
        next[editingItem.parentIndex] = {
          ...next[editingItem.parentIndex],
          label: editValue.trim(),
        };
      } else if (editingItem.type === 'subItem') {
        next[editingItem.parentIndex] = {
          ...next[editingItem.parentIndex],
          subItems: next[editingItem.parentIndex].subItems.map((subItem, idx) =>
            idx === editingItem.subItemIndex
              ? { ...subItem, label: editValue.trim() }
              : subItem
          ),
        };
      }
      return next;
    });

    cancelEditing();
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditing();
    }
  };

  const toggleExpand = (parentPath) => {
    setExpandedParents((prev) => ({
      ...prev,
      [parentPath]: !prev[parentPath],
    }));
  };

  // Drag handlers for parent items
  const handleParentDragStart = (e, parentIndex) => {
    if (parentIndex === 0) return; // Dashboard is not draggable
    setDraggedItem({ type: 'parent', parentIndex });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify({ type: 'parent', parentIndex }));
  };

  // Drag handlers for sub-items
  const handleSubItemDragStart = (e, parentIndex, subItemIndex) => {
    setDraggedItem({ type: 'subItem', parentIndex, subItemIndex });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify({ type: 'subItem', parentIndex, subItemIndex }));
  };

  const handleParentDragOver = (e, parentIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (!draggedItem) return;

    if (draggedItem.type === 'parent') {
      // Dragging a parent item
      if (parentIndex === 0 || parentIndex === draggedItem.parentIndex) return;
      setDropTarget({ type: 'parent', parentIndex, position: 'before' });
    } else if (draggedItem.type === 'subItem') {
      // Dragging a sub-item over a parent - can drop inside
      if (parentIndex === 0) return; // Can't drop on Dashboard
      // Auto-expand parent when dragging sub-item over it
      const parentPath = items[parentIndex]?.path;
      if (parentPath && !expandedParents[parentPath]) {
        setExpandedParents((prev) => ({ ...prev, [parentPath]: true }));
      }
      setDropTarget({ type: 'parent', parentIndex, position: 'inside' });
    }
  };

  const handleSubItemDragOver = (e, parentIndex, subItemIndex, position = 'after') => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (!draggedItem || draggedItem.type !== 'subItem') return;

    // Can drop before or after another sub-item
    if (draggedItem.parentIndex === parentIndex && draggedItem.subItemIndex === subItemIndex) return;

    setDropTarget({ type: 'subItem', parentIndex, subItemIndex, position });
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleParentDrop = (e, dropParentIndex) => {
    e.preventDefault();
    if (!draggedItem) return;

    if (draggedItem.type === 'parent') {
      // Moving a parent item
      if (dropParentIndex === 0 || dropParentIndex === draggedItem.parentIndex) return;

      setItems((prev) => {
        const next = [...prev];
        const [removed] = next.splice(draggedItem.parentIndex, 1);
        const insertAt = dropParentIndex > draggedItem.parentIndex ? dropParentIndex - 1 : dropParentIndex;
        next.splice(insertAt, 0, removed);
        return next;
      });
    } else if (draggedItem.type === 'subItem') {
      // Moving a sub-item to a new parent
      if (dropParentIndex === 0) return;

      setItems((prev) => {
        const next = [...prev];
        const sourceParent = next[draggedItem.parentIndex];
        const targetParent = next[dropParentIndex];

        if (!sourceParent || !targetParent) return prev;

        // Ensure target parent has subItems array
        if (!targetParent.subItems) {
          targetParent.subItems = [];
        }

        // Remove sub-item from source parent
        const [movedSubItem] = sourceParent.subItems.splice(draggedItem.subItemIndex, 1);

        // Add to target parent
        targetParent.subItems.push(movedSubItem);

        return [...next];
      });
    }

    setDraggedItem(null);
    setDropTarget(null);
  };

  const handleSubItemDrop = (e, dropParentIndex, dropSubItemIndex, position) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.type !== 'subItem') return;

    setItems((prev) => {
      const next = [...prev];
      const sourceParent = next[draggedItem.parentIndex];
      const targetParent = next[dropParentIndex];

      if (!sourceParent || !targetParent) return prev;

      // Remove sub-item from source
      const [movedSubItem] = sourceParent.subItems.splice(draggedItem.subItemIndex, 1);

      // Calculate insert position
      let insertIndex = dropSubItemIndex;
      if (position === 'after') {
        insertIndex = dropSubItemIndex + 1;
      }

      // If moving within the same parent, adjust index if needed
      if (draggedItem.parentIndex === dropParentIndex) {
        if (draggedItem.subItemIndex < dropSubItemIndex && position === 'after') {
          insertIndex = dropSubItemIndex; // Already accounted for removal
        } else if (draggedItem.subItemIndex < dropSubItemIndex && position === 'before') {
          insertIndex = dropSubItemIndex - 1;
        }
      }

      // Ensure target parent has subItems array
      if (!targetParent.subItems) {
        targetParent.subItems = [];
      }

      // Insert at target position
      targetParent.subItems.splice(insertIndex, 0, movedSubItem);

      return [...next];
    });

    setDraggedItem(null);
    setDropTarget(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDropTarget(null);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-h2 font-semibold text-[var(--ds-text-primary)]">Menu order</h2>
          <p className="text-meta text-[var(--ds-text-secondary)] mt-1">
            Drag and drop to reorder tenant sidebar menu items and sub-items. Dashboard is always first.
          </p>
        </div>
        <div className="rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] p-8 text-center text-meta text-[var(--ds-text-muted)]">
          Loading…
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ToastContainer />
      <div>
        <h2 className="text-h2 font-semibold text-[var(--ds-text-primary)]">Menu order</h2>
        <p className="text-meta text-[var(--ds-text-secondary)] mt-1">
          Drag and drop to reorder tenant sidebar menu items and sub-items. Dashboard is always first.
          Click parent items to expand/collapse sub-items.
        </p>
      </div>

      <div className="rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] divide-y divide-[var(--ds-border-divider)] bg-[var(--ds-surface-panel)]">
        {items.map((item, parentIndex) => {
          const isDashboard = parentIndex === 0;
          const isParentDragging = draggedItem?.type === 'parent' && draggedItem.parentIndex === parentIndex;
          const isParentDropTarget = dropTarget?.type === 'parent' && dropTarget.parentIndex === parentIndex;
          const hasSubItems = item.subItems && item.subItems.length > 0;
          const isExpanded = expandedParents[item.path] ?? false;

          return (
            <div key={item.path}>
              {/* Drop zone indicator before parent items */}
              {dropTarget?.type === 'parent' &&
                dropTarget.parentIndex === parentIndex &&
                dropTarget.position === 'before' && (
                  <div className="h-1 bg-[var(--ds-accent-primary)] mx-4 rounded" />
                )}

              {/* Parent Item */}
              <div
                draggable={!isDashboard}
                onDragStart={(e) => handleParentDragStart(e, parentIndex)}
                onDragOver={(e) => handleParentDragOver(e, parentIndex)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleParentDrop(e, parentIndex)}
                onDragEnd={handleDragEnd}
                className={`
                  flex items-center gap-3 px-4 py-3 transition-all duration-150
                  ${isParentDragging ? 'opacity-50' : ''}
                  ${isParentDropTarget && dropTarget.position === 'before' ? 'ring-2 ring-[var(--ds-accent-primary)] ring-inset bg-[var(--ds-control-field-bg)]' : ''}
                  ${isParentDropTarget && dropTarget.position === 'inside' ? 'ring-2 ring-[var(--ds-accent-primary)] ring-offset-2 bg-[var(--ds-control-field-bg)]' : ''}
                  ${!isDashboard ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}
                `}
              >
                {isDashboard ? (
                  <span className="w-6 flex-shrink-0" aria-hidden="true" />
                ) : (
                  <GripVertical
                    className="h-5 w-5 flex-shrink-0 text-[var(--ds-text-muted)] hover:text-[var(--ds-text-secondary)] transition-colors"
                    aria-label="Drag handle"
                  />
                )}

                {hasSubItems ? (
                  <button
                    type="button"
                    onClick={() => toggleExpand(item.path)}
                    className="flex items-center gap-2 flex-1 text-left"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-[var(--ds-text-muted)]" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-[var(--ds-text-muted)]" />
                    )}
                    {editingItem?.type === 'parent' && editingItem.parentIndex === parentIndex ? (
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={handleEditKeyDown}
                          onBlur={saveEdit}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          autoFocus
                          className="flex-1 px-2 py-1 text-body font-medium border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--ds-accent-primary)]"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            saveEdit();
                          }}
                          className="p-1 text-[var(--ds-status-success)] hover:bg-[var(--ds-control-field-bg)] rounded"
                          aria-label="Save"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            cancelEditing();
                          }}
                          className="p-1 text-[var(--ds-text-muted)] hover:bg-[var(--ds-control-field-bg)] rounded"
                          aria-label="Cancel"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="text-body font-medium text-[var(--ds-text-primary)]">{item.label}</span>
                        <span className="text-micro text-[var(--ds-text-muted)]">
                          ({item.subItems.length} {item.subItems.length === 1 ? 'item' : 'items'})
                        </span>
                      </>
                    )}
                  </button>
                ) : (
                  editingItem?.type === 'parent' && editingItem.parentIndex === parentIndex ? (
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={handleEditKeyDown}
                        onBlur={saveEdit}
                        autoFocus
                        className="flex-1 px-2 py-1 text-body font-medium border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--ds-accent-primary)]"
                      />
                      <button
                        type="button"
                        onClick={saveEdit}
                        className="p-1 text-[var(--ds-status-success)] hover:bg-[var(--ds-control-field-bg)] rounded"
                        aria-label="Save"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={cancelEditing}
                        className="p-1 text-[var(--ds-text-muted)] hover:bg-[var(--ds-control-field-bg)] rounded"
                        aria-label="Cancel"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <span className="flex-1 text-body font-medium text-[var(--ds-text-primary)]">{item.label}</span>
                  )
                )}

                {!isDashboard && !(editingItem?.type === 'parent' && editingItem.parentIndex === parentIndex) && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      startEditing('parent', parentIndex);
                    }}
                    className="p-1.5 text-[var(--ds-text-muted)] hover:text-[var(--ds-text-secondary)] hover:bg-[var(--ds-control-field-bg)] rounded transition-colors"
                    aria-label="Edit menu item name"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                )}

                {isDashboard && (
                  <span className="text-micro text-[var(--ds-text-muted)]">(always first)</span>
                )}
              </div>

              {/* Sub-items (when expanded) */}
              {hasSubItems && isExpanded && (
                <div className="bg-[var(--ds-control-field-bg)] border-t border-[var(--ds-border-divider)]">
                  {item.subItems.map((subItem, subItemIndex) => {
                    const isSubItemDragging =
                      draggedItem?.type === 'subItem' &&
                      draggedItem.parentIndex === parentIndex &&
                      draggedItem.subItemIndex === subItemIndex;
                    const isSubItemDropTarget =
                      dropTarget?.type === 'subItem' &&
                      dropTarget.parentIndex === parentIndex &&
                      dropTarget.subItemIndex === subItemIndex;

                    return (
                      <div
                        key={subItem.path}
                        draggable
                        onDragStart={(e) => handleSubItemDragStart(e, parentIndex, subItemIndex)}
                        onDragOver={(e) => {
                          handleSubItemDragOver(e, parentIndex, subItemIndex, 'after');
                        }}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleSubItemDrop(e, parentIndex, subItemIndex, 'after')}
                        onDragEnd={handleDragEnd}
                        className={`
                          flex items-center gap-3 px-4 py-2 pl-12 transition-all duration-150
                          ${isSubItemDragging ? 'opacity-50' : ''}
                          ${isSubItemDropTarget && dropTarget.position === 'before' ? 'border-t-2 border-[var(--ds-accent-primary)]' : ''}
                          ${isSubItemDropTarget && dropTarget.position === 'after' ? 'border-b-2 border-[var(--ds-accent-primary)]' : ''}
                          cursor-grab active:cursor-grabbing hover:bg-[var(--ds-surface-panel)]
                        `}
                      >
                        <GripVertical
                          className="h-4 w-4 flex-shrink-0 text-[var(--ds-text-muted)] hover:text-[var(--ds-text-secondary)] transition-colors"
                          aria-label="Drag handle"
                        />
                        {editingItem?.type === 'subItem' && 
                         editingItem.parentIndex === parentIndex && 
                         editingItem.subItemIndex === subItemIndex ? (
                          <div className="flex items-center gap-2 flex-1">
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={handleEditKeyDown}
                              onBlur={saveEdit}
                              autoFocus
                              className="flex-1 px-2 py-1 text-[12px] font-medium border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--ds-accent-primary)]"
                            />
                            <button
                              type="button"
                              onClick={saveEdit}
                              className="p-1 text-[var(--ds-status-success)] hover:bg-[var(--ds-surface-panel)] rounded"
                              aria-label="Save"
                            >
                              <Check className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={cancelEditing}
                              className="p-1 text-[var(--ds-text-muted)] hover:bg-[var(--ds-surface-panel)] rounded"
                              aria-label="Cancel"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <span className="flex-1 text-[12px] font-medium text-[var(--ds-text-secondary)]">
                              {subItem.label}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditing('subItem', parentIndex, subItemIndex);
                              }}
                              className="p-1 text-[var(--ds-text-muted)] hover:text-[var(--ds-text-secondary)] hover:bg-[var(--ds-surface-panel)] rounded transition-colors"
                              aria-label="Edit sub-menu item name"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}

                  {/* Drop zone indicator at the end of sub-items list */}
                  {dropTarget?.type === 'parent' &&
                    dropTarget.parentIndex === parentIndex &&
                    dropTarget.position === 'inside' && (
                      <div className="h-1 bg-[var(--ds-accent-primary)] mx-4 my-1 rounded" />
                    )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-3">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
        <Button variant="secondary" onClick={handleReset}>
          Reset to default
        </Button>
      </div>
    </div>
  );
}


